import { useEffect } from "react"
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"
import _ from "lodash-es"

import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import { InputGroup } from "react-bootstrap"
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from "../../components/ConfirmationModal"
import ConfirmationTaskModal from "../../components/ConfirmationTaskModal"
import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon"
import PageTitle from "../../components/PageTitle"
import TimestampWithUTCTooltip from "../../components/TimestampWithUTCTooltip"
import TasksInfoModal from './components/TasksInfoModal.jsx'
import TaskListFilteringModal from "./components/TaskListFilteringModal.jsx"


import { NotificationTypes, addNotification } from "../../stores/notification-store"
import { ExportTasks, DeleteTask } from "../../utils/api"

import { getDateMinus30DaysComputed, saveFile } from '../../utils/utils'

import { taskListSignal as tasks, taskListLoadingSignal as loading } from "../../state/signals/taskSignals.js"
import { fetchTaskList } from "../../state/actions/taskActions.js"
import { pageSizeSignal as rowsPerPageSignal, debugSignal, taskListFiltersValueSignal, taskListFiltersCountSignal, selectedBusinessEntityIdSignal } from "../../state/signals/userSettingsSignals.js"
import { removeTaskListFilter, updatePageSize, updateTaskListFilter, updateUtgListFilter } from "../../state/actions/userSettingsActions.js"

export default function TasksList() {

    const navigate = useNavigate()
    const { beId, componentId } = useParams()
    const [searchParams] = useSearchParams()
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData  } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary && userPermissionsSummary.value && !userPermissionsSummary.value.canViewTasks) {
            addNotification('You are not authorized to view Tasks for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value])
    
    const canEditSignal = useComputed(() => {
        return userPermissionsSummary?.value?.canEditTasks
    })

    const showDownloadProgress = useSignal(false)
    const downloadProgressMessage = useSignal('')
    const showExportConfirmationModal = useSignal(false)
    const showTasksInfoModalSignal = useSignal(false)

    const currentPage = useSignal(1)
    const filterString = useSignal("")
    const sortColumn = useSignal('')
    const isSelectAll = useSignal(false)
    const selectedRows = useSignal([])
    const selectedRowsCountLabel = useComputed(() => {
        return `(${selectedRows.value.length})`
    })

    const SortDirectionType = {
        ASCENDING: 1,
        DESCENDING: 2
    }
    const sortDirection = useSignal(SortDirectionType.DESCENDING)

    useEffect(() => {
        if (selectedBusinessEntityIdSignal.value !== beId) {
            updateTaskListFilter({ Predicates: [{ "PropertyName": "TaskRunAfter", "Operator": "GTE", "Value": getDateMinus30DaysComputed(), "TypeHint": "DateTime" }] })
        }
        fetchTaskList(beId, taskListFiltersValueSignal.value, config, accessToken)
    }, [beId])
    
    const filteredData = useComputed(() => {
        if (!filterString.value) {
            return tasks.value ? [...tasks.value] : []
        }

        // Convert the filter to lowercase
        const lowerCaseFilter = filterString.value.toLowerCase()

        // Filter the data based on the filter text
        return tasks.value.filter(item => {

            // Limit search to these fields
            let searchData = {
                ComponentMerchant: item.componentMerchant,
                ComponentNickname: item.componentNickname,
                UtgStatus: item.utgStatus,
                UtgVersion: item.utgVersion,
                AgentVersion: item.agentVersion,
                // ConfigStatus: item.ConfigStatus, // not visible on this page
                ComponentProfileName: item.componentProfileName,
                ComponentProfileVersion: item.componentProfileVersion,
                ComponentLocation: item.componentLocation,
                ComponentLane: item.componentLane,
                ComponentFileId: item.componentFileId,
                MachineDnsName: item.machineDnsName,
                MachineStatus: item.machineStatus,
                MachineStatusEntityLastKnownCommunication: item.machineStatusEntityLastKnownCommunication,
                MachineStatusEntityNextPredictedCommunication: item.machineStatusEntityNextPredictedCommunication,
                MachineNickname: item.machineNickname,
                BusinessEntityId: item.businessEntityId,
                StandardTimeZone: item.machineStatusEntityStandardTimeZone,
                WindowsVersion: item.machineStatusEntityWindowsVersion,

                ComponentPermanentId: item.componentPermanentId,
                MachinePermanentId: item.machinePermanentId,

                TaskBulkLabel: item.taskBulkLabel,
                TaskId: item.taskId,
                TaskLastUpdatedAt: item.taskLastUpdatedAt,
                TaskRunAfter: item.taskRunAfter,
                TaskStatusName: item.taskStatusName,
                TaskTypeName: item.taskTypeName
            }

            return Object.values(searchData).some(value => {
                if (value && (typeof (value) == 'string')) {
                    return value.toString().toLowerCase().includes(lowerCaseFilter)
                }
            })
        })
    })

    const sortedData = useComputed(() => {

        // just return filtered data if no sorting
        if (!sortColumn.value.length) {
            return [...filteredData.value]
        }

        let _sortedData = [...filteredData.value]
        return _sortedData.sort((a, b) => {
            let _a = _.get(a, sortColumn.value, null)
            let _b = _.get(b, sortColumn.value, null)

            // Convert nulls to empty strings for sorting purposes
            _a = _a ? _a : ''
            _b = _b ? _b : ''

            // convert values to lowercase
            if (_a && _a.length) {
                _a = _a.toLowerCase()
            }
            if (_b && _b.length) {
                _b = _b.toLowerCase()
            }

            if (sortDirection.value == SortDirectionType.DESCENDING) {
                if (_a < _b) return -1
                if (_a > _b) return 1
                return 0
            }
            else {
                if (_a > _b) return -1
                if (_a < _b) return 1
                return 0
            }
        })
    })

    const totalPages = useComputed(() => {
        return Math.ceil(filteredData.value.length / parseInt(rowsPerPageSignal.value, 10))
    })

    const displayData = useComputed(() => {
        // Calculate the starting and ending indexes for pagination
        const startIndex = (currentPage.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value

        return sortedData.value.slice(startIndex, endIndex)
    })
    const startDisplayRowNumber = useComputed(() => {
        return ((currentPage.value - 1) * rowsPerPageSignal.value) + 1
    })
    const endDisplayRowNumber = useComputed(() => {
        const startIndex = (currentPage.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value
        return endIndex < filteredData.value.length ? endIndex : filteredData.value.length
    })

    const accordionData = [
        { name: 'Tasks', data: tasks },
        { name: 'Display Data', data: displayData },
        { name: 'Sorted Data', data: sortedData },
        { name: 'Filtered Data', data: filteredData },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    const handlePageSizeSelect = (v) => {
        currentPage.value = 1
        updatePageSize(parseInt(v, 10))
    }
    const handleFilterStringChange = (e) => {
        currentPage.value = 1
        filterString.value = e.currentTarget.value
    }
    const handleCopyClipboard = item => {
        if (item) {
            // place the data on the users clipboard if the browser supports it
            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(JSON.stringify(item, null, 2));
                addNotification(`The data for Task Id: ${item.taskId} (${item.taskTypeName}) has been copied to your clipboard.`, NotificationTypes.SUCCESS, false, 'Copy Succeeded')
            }
            else {
                addNotification('This browser does not support copying data to the clipboard.', NotificationTypes.INFO, false, 'Copy Failed')
            }
        }
    }

    // Task Filtering Modal
    const showTaskFilteringModalSignal = useSignal(false)
    const handleCancelTaskFiltering = () => showTaskFilteringModalSignal.value = false
    const handleApplyTaskFilters = (predicates) => {
        showTaskFilteringModalSignal.value = false
        updateTaskListFilter(predicates)
        fetchTaskList(beId, predicates, config, accessToken)
    }
    const handleClearFilter = () => {
        updateTaskListFilter({ Predicates: [{ "PropertyName": "TaskRunAfter", "Operator": "GTE", "Value": getDateMinus30DaysComputed(), "TypeHint": "DateTime" }] })
        fetchTaskList(beId, taskListFiltersValueSignal.value, config, accessToken)
    }

    const showRemoveTaskModal = useSignal(false)
    const handleCancelRemoveTask = () => showRemoveTaskModal.value = false
    const handleSubmitModalRemoveTask = () => {
        showRemoveTaskModal.value = false

        let promises = []
        for (let i = 0; i < selectedRows.value.length; i++) {
            try {
                promises.push(DeleteTask(selectedRows.value[i].businessEntityId, selectedRows.value[i].machinePermanentId, selectedRows.value[i].taskId, config, accessToken))
            } catch (error) {
                addNotification(`Unable to remove all selected Tasks`, NotificationTypes.ERROR, false, 'Remove Tasks Failed')
            }
        }
        Promise.all(promises)
            .then(async (results) => {
                addNotification('The selected Tasks were successfully removed.', NotificationTypes.SUCCESS, false)
                fetchTaskList(beId, taskListFiltersValueSignal.value, config, accessToken)
            })
            .catch(error => {
                addNotification(`Unable to remove all selected Tasks`, NotificationTypes.ERROR, false, 'Remove Tasks Failed')
            })
    }

    const handleRemoveTask = (item) => {
        if (item) {
            // if array does not contain item, add item
            if (!selectedRows.value.includes(item)) {
                selectedRows.value = [...selectedRows.value, item]
            }
            showRemoveTaskModal.value = true
        }
    }
    const handleTaskExport = async () => {
        // set showDownloadProgress to true to show spinner
        showDownloadProgress.value = true
        downloadProgressMessage.value = 'Preparing to export tasks'
        try {
            const exportTasksResponse = await ExportTasks(beId, config, accessToken)
            saveFile(exportTasksResponse, "tasks.csv")
            addNotification('Exporting tasks. Your download should begin shortly.', NotificationTypes.SUCCESS, true)
            showDownloadProgress.value = false
        }
        catch (error) {
            console.log(error)
            addNotification(`Unable to export tasks. ${error}`, NotificationTypes.ERROR, true)
            showDownloadProgress.value = false
        }
    }

    const handleSelectAll = () => {

        if (isSelectAll.value) {
            // deselect all
            selectedRows.value = []
            isSelectAll.value = false
            return
        }

        // select all rows from filtered data
        isSelectAll.value = true
        selectedRows.value = filteredData.value.map(item => {
            return item
        })
    }

    const handleSelectRow = (item) => {
        // Check if the item is already selected
        const wasItemSelected = selectedRows.value.some(v => v.taskId === item.taskId);

        if (wasItemSelected) {
            // User wants to deselect, so filter out the item
            selectedRows.value = selectedRows.value.filter(v => v.taskId !== item.taskId);
        } else {
            // Add the item to the selectedRows array
            selectedRows.value = [...selectedRows.value, item];
        }
    }

    const handleHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == sortColumn.value) {
            sortDirection.value = sortDirection.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        sortColumn.value = datafield
    }

    function handleCancelModalTasksInfo() {
        showTasksInfoModalSignal.value = false
    }

    function TableRows({ items }) {
        if (items && items.length) {
            const rows = items.map(item => {
                return <TableRow item={item} key={`${item.taskId}`} />
            })

            return (
                <tbody>{rows}</tbody>
            )
        }
        else if (items) {
            return (
                <tbody><tr><td colSpan="23" className="text-center">No tasks found</td></tr></tbody>
            )
        }
    }

    function TableRow({ item }) {
        return (
            <tr>
                <td><Form.Check aria-label="Select Row" checked={selectedRows.value.some(row => row.taskId === item.taskId)} onChange={() => handleSelectRow(item)} /></td>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions">
                            <Icon icon="gear" height={16} width={16}></Icon>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleCopyClipboard(item)}>Copy Row as JSON</Dropdown.Item>
                            {canEditSignal.value && <Dropdown.Item onClick={() => handleRemoveTask(item)}>Delete Task</Dropdown.Item>}
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td>{item.componentMerchant}</td>
                <td>{item.machineDnsName}</td>
                <td><Link to={`../../be/${item.businessEntityId}/utgs/${item.componentId}`}>{item.componentNickname}</Link></td>
                <td>{item.taskTypeName}</td>
                <td><TimestampWithUTCTooltip date={item.taskRunAfter}></TimestampWithUTCTooltip></td>
                <td><TimestampWithUTCTooltip date={item.taskLastUpdatedAt}></TimestampWithUTCTooltip></td>
                <td>{item.taskStatusName}</td>
                <td>{item.machineStatus}</td>
                <td>{item.utgStatus}</td>
                <td><TimestampWithUTCTooltip date={item.machineStatusEntityLastKnownCommunication}></TimestampWithUTCTooltip></td>
                <td><TimestampWithUTCTooltip date={item.machineStatusEntityNextPredictedCommunication}></TimestampWithUTCTooltip></td>
                <td>{item.utgVersion}</td>
                <td>{item.agentVersion}</td>
                <td>{item.componentProfileName}</td>
                <td>{item.componentProfileVersion}</td>
                <td>{item.componentFileId}</td>
                <td>{item.componentLocation}</td>
                <td>{item.componentLane}</td>
                <td>{item.machineNickname}</td>
                <td>{item.taskBulkLabel}</td>
                <td>{item.parentName}</td>
                <td>{item.machineStatusEntityStandardTimeZone}</td>
                <td>{item.machineStatusEntityWindowsVersion}</td>
            </tr>
        )
    }

    function PaginationItems() {
        let items = []
        for (let i = 1; i <= totalPages.value; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPage.value} onClick={() => currentPage.value = i}>{i}</Pagination.Item>
            )
        }
        return items
    }

    function TableColumnHeader({ title, sortable, datafield }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumn.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirection.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                    <Icon icon="caret-down" width="16" height="16"></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width="16" height="16"></Icon>
            </th>
        )
    }

    function DownloadProgress() {
        if (showDownloadProgress.value) {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center">
                    <Spinner className="mt-5" animation="border" role="status">
                        <span className="visually-hidden">{downloadProgressMessage.value}</span>
                    </Spinner>
                    <p className="mt-3">{downloadProgressMessage.value}</p>
                </div>
            )
        }
    }

    if (loading.value || !tasks.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
            <DownloadProgress></DownloadProgress>
            <ConfirmationModal show={showExportConfirmationModal.value} handleCancel={() => showExportConfirmationModal.value = false} handleConfirm={() => { showExportConfirmationModal.value = false; handleTaskExport(); }} message={'Export may take a few minutes to download ALL data on this tab. Are you sure you want to continue?'} title={"Export Tasks"} />
            <ConfirmationTaskModal show={showRemoveTaskModal.value} handleCancel={() => handleCancelRemoveTask()} handleConfirm={() => { handleSubmitModalRemoveTask(); }} selectedTasks={selectedRows} />
            <PageTitle title='Tasks List' canEditSignal={canEditSignal} />
            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <Stack direction="horizontal" gap={3} className="me-auto">
                    <Nav.Item>
                        <InputGroup>
                            <Button size="md" variant="outline-secondary" onClick={() => handleClearFilter()}><Icon icon='trash' width="16" height="16"></Icon></Button>
                            <Button size="md" variant="outline-secondary" onClick={() => showTaskFilteringModalSignal.value = true}><Icon icon='funnel' width="16" height="16"></Icon>&nbsp;Filters ({taskListFiltersCountSignal.value})</Button>
                        </InputGroup>
                    </Nav.Item>
                    <DropdownButton title={`${rowsPerPageSignal} records per page`} id="pageSize-dropdown" variant="outline-secondary" onSelect={handlePageSizeSelect} className="me-auto">
                        <Dropdown.Item eventKey="10">10</Dropdown.Item>
                        <Dropdown.Item eventKey="25">25</Dropdown.Item>
                        <Dropdown.Item eventKey="50">50</Dropdown.Item>
                        <Dropdown.Item eventKey="250">250</Dropdown.Item>
                        <Dropdown.Item eventKey="500">500</Dropdown.Item>
                        <Dropdown.Item eventKey="1000">1000</Dropdown.Item>
                    </DropdownButton>
                </Stack>
                <Nav.Item className="mt-1 me-2" style={{maxWidth:"calc(100% - 407.25px)"}}>
                    <Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" onChange={handleFilterStringChange} />
                </Nav.Item>
                <Nav.Item>
                    <Button variant="outline-secondary" onClick={() => { showTasksInfoModalSignal.value = true }}>
                        <Icon icon="info-circle" height={16} width={16}></Icon>
                    </Button>
                    &nbsp;
                </Nav.Item>
                <Nav.Item className="mt-1">
                    <Button size="sm" variant="outline-secondary" onClick={() => showExportConfirmationModal.value = true}><Icon icon='download' width="16" height="16"></Icon>&nbsp;Download data as CSV</Button>
                </Nav.Item>
            </Nav >
            <Table responsive striped hover size="sm" className="cc-table">
                <thead>
                    <tr>
                        <td><Form.Check type="checkbox" id="select-all-switch" aria-label="Select All" label={selectedRowsCountLabel.value} checked={isSelectAll.value} onChange={() => handleSelectAll()} /></td>
                        <TableColumnHeader title={'Action'} sortable={false} datafield={''}></TableColumnHeader>
                        <TableColumnHeader title={'Merchant'} sortable={true} datafield={'componentMerchant'}></TableColumnHeader>
                        <TableColumnHeader title={'Machine DNS Name'} sortable={true} datafield={'machineDnsName'}></TableColumnHeader>
                        <TableColumnHeader title={'UTG Nickname'} sortable={true} datafield={'componentNickname'}></TableColumnHeader>
                        <TableColumnHeader title={'Task Type'} sortable={true} datafield={'taskTypeName'}></TableColumnHeader>
                        <TableColumnHeader title={'Task Run Date'} sortable={true} datafield={'taskRunAfter'}></TableColumnHeader>
                        <TableColumnHeader title={'Task Updated'} sortable={true} datafield={'taskLastUpdatedAt'}></TableColumnHeader>
                        <TableColumnHeader title={'Task State'} sortable={true} datafield={'taskStatusName'}></TableColumnHeader>
                        <TableColumnHeader title={'Machine Status'} sortable={true} datafield={'machineStatus'}></TableColumnHeader>
                        <TableColumnHeader title={'UTG Status'} sortable={true} datafield={'utgStatus'}></TableColumnHeader>
                        <TableColumnHeader title={'Last Known Communication'} sortable={true} datafield={'machineStatusEntityLastKnownCommunication'}></TableColumnHeader>
                        <TableColumnHeader title={'Next Predicted Communication'} sortable={true} datafield={'machineStatusEntityNextPredictedCommunication'}></TableColumnHeader>
                        <TableColumnHeader title={'UTG Version'} sortable={true} datafield={'utgVersion'}></TableColumnHeader>
                        <TableColumnHeader title={'Agent Version'} sortable={true} datafield={'agentVersion'}></TableColumnHeader>
                        <TableColumnHeader title={'Profile'} sortable={true} datafield={'componentProfileName'}></TableColumnHeader>
                        <TableColumnHeader title={'Profile Version'} sortable={true} datafield={'componentProfileVersion'}></TableColumnHeader>
                        <TableColumnHeader title={'File Id'} sortable={true} datafield={'componentFileId'}></TableColumnHeader>
                        <TableColumnHeader title={'Location/Serial'} sortable={true} datafield={'componentLocation'}></TableColumnHeader>
                        <TableColumnHeader title={'Lane'} sortable={true} datafield={'componentLane'}></TableColumnHeader>
                        <TableColumnHeader title={'Machine Nickname'} sortable={true} datafield={'machineNickname'}></TableColumnHeader>
                        <TableColumnHeader title={'Task Label'} sortable={true} datafield={'taskBulkLabel'}></TableColumnHeader>
                        <TableColumnHeader title={'Parent Account'} sortable={true} datafield={'parentName'}></TableColumnHeader>
                        <TableColumnHeader title={'Time Zone'} sortable={true} datafield={'machineStatusEntityStandardTimeZone'}></TableColumnHeader>
                        <TableColumnHeader title={'Windows Version'} sortable={true} datafield={'machineStatusEntityWindowsVersion'}></TableColumnHeader>
                    </tr>
                </thead>
                <TableRows items={displayData.value} />
                {displayData.value.length > 0 && <tfoot><tr><td className="fs-6" colSpan={19}>Displaying {startDisplayRowNumber.value}-{endDisplayRowNumber.value} of {filteredData.value.length}</td></tr></tfoot>}
            </Table>
            <Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
            <DebugAccordion data={accordionData} debug={debugSignal} />

            <TaskListFilteringModal
                show={showTaskFilteringModalSignal.value}
                handleCancel={handleCancelTaskFiltering}
                handleSubmit={handleApplyTaskFilters}
            ></TaskListFilteringModal>

            <TasksInfoModal
                showSignal={showTasksInfoModalSignal}
                handleCancel={handleCancelModalTasksInfo}
                referenceGuid={config && config.value && config.value.referenceGuid ? config.value.referenceGuid : null}
            ></TasksInfoModal>
        </>
    )
}